import { Link } from "react-router-dom";
import Room from "@/components/Room";

export default function FoodSupply() {
  return (
    <Room title="Food Supply" description="What happens to a food supply when a planted landscape ages all at once.">
      <div className="fade">
        <div className="kicker">Food Supply · Fractal</div>
        <h1 data-testid="food-supply-h1">Hectares do not equal supply.</h1>
        <div className="goldrule"></div>
        <p className="lede">A plantation map counts trees. It does not count which still bear fruit, who still climbs them, or how long either will last. Every supply forecast built on the map inherits all three gaps.</p>
      </div>

      <section className="q">
        <div className="n">01</div>
        <h2>Is a planted hectare a productive hectare?</h2>
        <p>Land cover maps count canopy. A crown that has stopped fruiting looks the same from above as one that has not. The brownfield coconut blocks this work starts from carry around 1,500 palms on ten hectares, and around 900 of them no longer fruit.</p>
        <p>The map records ten productive hectares. The ground supports something closer to four. Which of those two numbers is your supply forecast built on?</p>
      </section>

      <section className="q">
        <div className="n">02</div>
        <h2>Why does the farmer not replant?</h2>
        <p>A coconut palm takes five to seven years to bear. Fell the old trees and the income stops for the length of that lag, and no smallholder can finance seven years without income. So the old trees stay standing, yielding a little less each year, and the replant never happens.</p>
        <p>Meanwhile the farmer ages with the trees and the next generation leaves for the city. The supply does not collapse in a season. It thins quietly until the land is abandoned, and the statistics record it years after the buyers who were watching have moved.</p>
      </section>

      <section className="q">
        <div className="n">03</div>
        <h2>What if the old tree is not waste?</h2>
        <p>The fractal starts by removing dead capacity and selling it. Senile trunks become charcoal and wood vinegar from the first month. The palms still fruiting are kept as bridge income and shade while hybrids grow beneath them, and shade crops such as cacao fill the years before the new canopy bears.</p>
        <p>The trough the farmer cannot cross is financed by the very thing that caused it: the old trees. Is there anything in your supply chain currently burned that could carry its own replacement?</p>
      </section>

      <section className="q">
        <div className="n">04</div>
        <h2>Why ten hectares?</h2>
        <p>Ten hectares is the smallest unit that carries a full transition, one trained local manager and a daily record, without consolidating land or displacing the people on it. It does not wait for an estate to be assembled. It replicates village by village.</p>
        <ul className="plain">
          <li>Every site reports daily: the work done, tree observations, yields, local prices, biomass and the knowledge of the people who live there.</li>
          <li>Each site tests a different product path: coconut with cacao, husk into coir and growing media, biomass into pellets or biochar, green nuts, or sap and sugar.</li>
          <li>The field decides which product stack wins. The plan does not.</li>
        </ul>
        <p>Daily data becomes weekly intelligence, weekly intelligence becomes management, and management becomes method.</p>
      </section>

      <section className="q">
        <div className="n">05</div>
        <h2>Who keeps the value?</h2>
        <p>A system that exports its value exports its people soon after, and when the people leave the supply goes with them. The fractal is built so that value stays in the village, because continuity of the people on the land is the only thing that keeps a food system producing. Local knowledge is part of the method. It is not an input to be taken.</p>
      </section>

      <section className="close">
        <p className="ask">Your contracts name a volume. If the trees and the people behind that volume were dated, which supply would you lock in now, and which would you replace before the market notices?</p>
        <Link className="write" to="/enter/food-supply" data-testid="food-supply-write">Write</Link>
      </section>
    </Room>
  );
}
